import { ContractStandardField } from "../../entityTemplates/EntityTemplate.types.ts";
import { ContractModel } from "../contract/Contract.types.ts";
import { ContractExtractionResponse, ExtractedStandardField } from "../addContractButton/createContractPane/CreateContractPane.types.ts";
import { mapCustomFieldToCustomFieldValue, mapStandardFieldToContractModel } from "../addContractButton/createContractPane/extractionAdapter.ts";
import { isEmptyFieldValue } from "./contractFieldValue.ts";

/** Standard fields whose proposal is an id the backend resolved, not the text the model wrote. */
const RESOLVED_ID_FIELDS = new Set<ContractStandardField>([
    ContractStandardField.ContractType,
    ContractStandardField.PaymentTerm,
    ContractStandardField.ContractManager,
]);

const RESOLVED_ID_LIST_FIELDS = new Set<ContractStandardField>([
    ContractStandardField.BusinessAreas,
    ContractStandardField.BusinessAreaAccess,
    ContractStandardField.LegalEntities,
    ContractStandardField.AdditionalUserAccess,
    ContractStandardField.ExcludedUsers,
]);

/**
 * The contract as the review proposes it: the current one, with the fields the AI actually found written
 * over the top. A field the AI said nothing about keeps the value the contract already has, which is what
 * lets the proposed column stand on its own as the thing that will be saved. Only the standard fields the
 * review shows are written at all: the extraction runs against the whole template, so it can propose a value
 * for a field this user's role hides or may only read, and that must not reach the save unseen.
 */
export function applyExtractionToContract(
    current: ContractModel,
    extraction: ContractExtractionResponse,
    reviewableStandardFields: Set<ContractStandardField>
): ContractModel {
    const proposed = cloneContract(current);

    for (const field of extraction.standardFields) {
        if (reviewableStandardFields.has(field.fieldIdentifier) && hasStandardFieldProposal(field)) {
            mapStandardFieldToContractModel(field, proposed);
        }
    }

    for (const field of extraction.customFields) {
        const mapped = mapCustomFieldToCustomFieldValue(field);
        // The mapper turns a multi-select the AI found nothing for into an empty list, which creation needs.
        // Here that list would clear the values the contract already has, so it counts as no finding.
        if (isEmptyFieldValue(mapped.value)) {
            continue;
        }

        const existing = proposed.customFieldValues.find((value) => value.customInputFieldId === mapped.customInputFieldId);
        if (existing) {
            existing.value = mapped.value;
        } else {
            proposed.customFieldValues.push(mapped);
        }
    }

    return proposed;
}

/**
 * A spread rather than structuredClone: ContractModel is a class, and structuredClone would hand back a
 * plain object without its prototype. Every array is rebuilt so editing the proposal cannot reach back
 * into the contract the page is still showing.
 */
export function cloneContract(contract: ContractModel): ContractModel {
    return {
        ...contract,
        businessAreas: [...contract.businessAreas],
        contractAccessBusinessAreas: [...contract.contractAccessBusinessAreas],
        groupEntities: [...contract.groupEntities],
        additionalAccessUsers: [...contract.additionalAccessUsers],
        excludedUsers: [...contract.excludedUsers],
        counterParties: contract.counterParties.map((party) => ({ ...party, subContractors: [...party.subContractors] })),
        customFieldValues: contract.customFieldValues.map((value) => ({ ...value })),
    };
}

/**
 * Whether the model actually found this field. A field that resolves to ids needs the resolution to have
 * matched something: the model naming two business areas the customer does not have is not a reason to
 * clear the ones the contract already carries.
 */
export function hasStandardFieldProposal(field: ExtractedStandardField): boolean {
    if (RESOLVED_ID_FIELDS.has(field.fieldIdentifier)) {
        return field.resolvedId !== undefined && field.resolvedId !== null;
    }

    if (RESOLVED_ID_LIST_FIELDS.has(field.fieldIdentifier)) {
        return (field.resolvedIds?.length ?? 0) > 0;
    }

    return field.extractedValue !== undefined && field.extractedValue !== null && field.extractedValue.trim().length > 0;
}
